/**
 * `POST /ai-support/chat` over fetch + ReadableStream (not EventSource: it
 * can't POST or send the bearer token). Framework-free (copied to next-mapit).
 */

import { ChatApiError, parseEnvelopeCode, parseRetryAfter } from "./errors";
import { ERROR_CODES } from "./protocol";
import { createSseParser } from "./sseParser";

/**
 * @typedef {import("./sseParser").SseEvent} SseEvent
 */

/**
 * @typedef {Object} StreamChatOptions
 * @property {string} url
 * @property {Record<string, string>} headers
 * @property {Object} body JSON-serialisable `{ conversationId?, clientMessageId, message }`.
 * @property {AbortSignal} [signal] Aborting (Stop) is not an error: the promise resolves.
 * @property {(event: SseEvent) => void} onEvent Called per SSE frame, never after an abort.
 * @property {typeof fetch} [fetchImpl] Defaults to the global `fetch`.
 */

const isAbort = (error, signal) =>
  error?.name === "AbortError" || Boolean(signal?.aborted);

const networkError = (error, httpStatus = null, retryAfterSeconds = null) =>
  new ChatApiError({
    code: ERROR_CODES.NETWORK,
    httpStatus,
    message: (error && error.message) || "",
    retryAfterSeconds,
  });

/**
 * Turns a non-2xx response into a ChatApiError. The app code is the JSON
 * envelope `status` (851–858), never the HTTP status; a body that isn't our
 * envelope (proxy HTML, gateway 502…) is a network error.
 *
 * @param {Response} response
 * @returns {Promise<ChatApiError>}
 */
async function toChatApiError(response) {
  const httpStatus = response.status;
  // Readable cross-origin only if the server exposes it; null otherwise.
  const retryAfterSeconds = parseRetryAfter(
    response.headers?.get?.("Retry-After"),
  );

  let json = null;
  try {
    json = JSON.parse(await response.text());
  } catch {
    json = null;
  }

  const code = parseEnvelopeCode(json?.status);
  if (code === null) {
    return networkError(null, httpStatus, retryAfterSeconds);
  }
  return new ChatApiError({
    code,
    httpStatus,
    message: typeof json.message === "string" ? json.message : "",
    retryAfterSeconds,
  });
}

/**
 * Sends one chat message and feeds the SSE response to `onEvent`.
 *
 * - Resolves when the stream ends. Ending without a `done` event is for the
 *   caller to judge (→ `failed`).
 * - Resolves quietly on abort; no event is delivered after it.
 * - Rejects with a ChatApiError: the app code for a non-2xx JSON envelope
 *   (with `retryAfterSeconds` from `Retry-After`), else `"network"`.
 *
 * Reads with `getReader()` (Safari can't async-iterate a ReadableStream) and
 * decodes with `{ stream: true }` so a character split across chunks survives.
 *
 * @param {StreamChatOptions} options
 * @returns {Promise<void>}
 */
export async function streamChat({
  url,
  headers,
  body,
  signal,
  onEvent,
  fetchImpl,
}) {
  if (signal?.aborted) return;

  let response;
  try {
    // Resolved here so a missing fetch becomes a network error, not a throw.
    response = await (fetchImpl || fetch)(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (isAbort(error, signal)) return;
    throw networkError(error);
  }

  if (!response.ok) {
    const error = await toChatApiError(response);
    if (signal?.aborted) return;
    throw error;
  }

  if (typeof response.body?.getReader !== "function") {
    throw networkError(
      new Error("Response has no readable body"),
      response.status,
    );
  }

  const reader = response.body.getReader();
  const cancel = () => {
    if (typeof reader.cancel === "function") {
      Promise.resolve(reader.cancel()).catch(() => {});
    }
  };
  // fetch errors the body on abort, but don't rely on every implementation:
  // cancelling settles a pending read() so the loop can exit.
  signal?.addEventListener?.("abort", cancel);

  const decoder = new TextDecoder("utf-8");
  const parser = createSseParser((event) => {
    if (!signal?.aborted) onEvent(event);
  });

  let finished = false;
  try {
    if (signal?.aborted) return;
    for (;;) {
      let result;
      try {
        result = await reader.read();
      } catch (error) {
        if (isAbort(error, signal)) return;
        throw networkError(error, response.status);
      }
      if (signal?.aborted) return;
      if (result.done) break;
      parser.feed(decoder.decode(result.value, { stream: true }));
    }
    finished = true;
    parser.feed(decoder.decode());
    parser.flush();
  } finally {
    signal?.removeEventListener?.("abort", cancel);
    // Release the connection when we stop early (abort, consumer threw).
    if (!finished) cancel();
  }
}
